import React, { useState, useEffect } from "react";
import { useUserGroups } from "../../hooks/useUserGroups";
import {
  Box,
  Button,
  Paper,
  Typography,
  IconButton,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Alert,
  Tooltip,
  Menu,
  MenuItem,
  ListItemIcon,
  ListItemText,
} from "@mui/material";
import { useLazyQuery } from "@apollo/client";
import {
  useAuthenticatedQuery,
  useAuthenticatedMutation,
} from "../../hooks/useAuthenticatedQuery";
import { useNavigate } from "react-router-dom";
import AddIcon from "@mui/icons-material/Add";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import PersonAddIcon from "@mui/icons-material/PersonAdd";
import LoginIcon from "@mui/icons-material/Login";
import BlockIcon from "@mui/icons-material/Block";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import {
  LIST_EMPLOYEES,
  DELETE_EMPLOYEE,
  CREATE_USER,
  CHECK_USER_EXISTS,
  ENABLE_USER,
  DISABLE_USER,
} from "../../graphql/operations";
import { Employee } from "../../types/employee";
import { Helmet } from "react-helmet-async";
import QRCode from "react-qr-code";

interface UserStatus {
  username: string;
  userStatus: string;
}

const EmployeeList: React.FC = () => {
  const navigate = useNavigate();
  const { isAdmin, loading: groupsLoading } = useUserGroups();
  const { data, loading, error, refetch } = useAuthenticatedQuery(
    LIST_EMPLOYEES,
    {
      skip: !isAdmin, // Skip query if user is not admin
    }
  );

  const [deleteEmployee] = useAuthenticatedMutation(DELETE_EMPLOYEE);
  const [createUser] = useAuthenticatedMutation(CREATE_USER);
  const [enableUser] = useAuthenticatedMutation(ENABLE_USER);
  const [disableUser] = useAuthenticatedMutation(DISABLE_USER);
  const [checkUserExists] = useLazyQuery(CHECK_USER_EXISTS);

  const [qrDialogOpen, setQrDialogOpen] = useState(false);
  const [currentEmployee, setCurrentEmployee] = useState<Employee | null>(null);
  const [userCredentials, setUserCredentials] = useState<{
    username: string;
    password: string;
    loginUrl: string;
  } | null>(null);
  const [loadingCreate, setLoadingCreate] = useState(false);
  const [userExistsMap, setUserExistsMap] = useState<Record<string, boolean>>(
    {}
  );
  const [userStatusMap, setUserStatusMap] = useState<
    Record<string, UserStatus>
  >({});
  const [checkingUsers, setCheckingUsers] = useState(false);
  const [menuAnchorEl, setMenuAnchorEl] = useState<null | HTMLElement>(null);
  const [selectedUser, setSelectedUser] = useState<string>("");

  const employees = data?.listEmployees?.items || [];

  // Check if users exist and get their status
  useEffect(() => {
    const checkAllUsers = async () => {
      if (employees.length > 0) {
        setCheckingUsers(true);
        const checks = employees.map(async (employee: Employee) => {
          const username =
            `${employee.lastName}.${employee.firstName}`.toLowerCase();
          try {
            const { data } = await checkUserExists({
              variables: { username },
            });

            if (data?.checkUserExists) {
              // If user exists, we assume they're enabled by default
              // You might want to add a separate query to get actual status
              return {
                username,
                exists: true,
                status: { username, userStatus: "ENABLED" }, // Default status
              };
            }
            return { username, exists: false, status: null };
          } catch (error) {
            console.error(`Error checking user ${username}:`, error);
            return { username, exists: false, status: null };
          }
        });

        try {
          const results = await Promise.all(checks);
          const newUserExistsMap: Record<string, boolean> = {};
          const newUserStatusMap: Record<string, UserStatus> = {};

          results.forEach((result) => {
            newUserExistsMap[result.username] = result.exists;
            if (result.status) {
              newUserStatusMap[result.username] = result.status;
            }
          });

          setUserExistsMap(newUserExistsMap);
          setUserStatusMap(newUserStatusMap);
        } catch (error) {
          console.error("Error checking users:", error);
        } finally {
          setCheckingUsers(false);
        }
      }
    };

    checkAllUsers();
  }, [employees, checkUserExists]);

  const handleDelete = async (id: string) => {
    if (window.confirm("Are you sure you want to delete this employee?")) {
      try {
        await deleteEmployee({ variables: { id } });
        refetch();
      } catch (error) {
        console.error("Error deleting employee:", error);
      }
    }
  };

  const handleEnableUser = async (username: string) => {
    try {
      const result = await enableUser({ variables: { username } });
      setUserStatusMap((prev) => ({
        ...prev,
        [username]: result.data.enableUser,
      }));
      setMenuAnchorEl(null);
    } catch (error: any) {
      console.error("Error enabling user:", error);
      alert(error.message);
    }
  };

  const handleDisableUser = async (username: string) => {
    try {
      const result = await disableUser({ variables: { username } });
      setUserStatusMap((prev) => ({
        ...prev,
        [username]: result.data.disableUser,
      }));
      setMenuAnchorEl(null);
    } catch (error: any) {
      console.error("Error disabling user:", error);
      alert(error.message);
    }
  };

  const handleCreateLogin = async (employee: Employee) => {
    setLoadingCreate(true);
    setCurrentEmployee(employee);

    try {
      const result = await createUser({
        variables: {
          input: {
            email: employee.email,
            firstName: employee.firstName,
            lastName: employee.lastName,
            group: "User",
            userId: employee.id,
            username:
              `${employee.lastName}.${employee.firstName}`.toLowerCase(),
          },
        },
      });

      const { username, temporaryPassword } = result.data.createUser;
      const loginUrl = `${window.location.origin}/dashboard/login`;

      setUserCredentials({
        username: username,
        password: temporaryPassword,
        loginUrl: loginUrl,
      });

      // Update the user exists and status maps
      setUserExistsMap((prev) => ({ ...prev, [username]: true }));
      setUserStatusMap((prev) => ({
        ...prev,
        [username]: {
          username,
          userStatus: "FORCE_CHANGE_PASSWORD",
        },
      }));

      setQrDialogOpen(true);
    } catch (error: any) {
      console.error("Error creating user:", error);
      alert(error.message);
    } finally {
      setLoadingCreate(false);
    }
  };

  const handleShowLogin = (employee: Employee) => {
    const username = `${employee.lastName}.${employee.firstName}`.toLowerCase();
    const loginUrl = `${window.location.origin}/dashboard/login`;

    setUserCredentials({
      username: username,
      password: "Use 'Forgot Password' if needed",
      loginUrl: loginUrl,
    });
    setCurrentEmployee(employee);
    setQrDialogOpen(true);
  };

  const handleMenuOpen = (
    event: React.MouseEvent<HTMLElement>,
    username: string
  ) => {
    setMenuAnchorEl(event.currentTarget);
    setSelectedUser(username);
  };

  const handleMenuClose = () => {
    setMenuAnchorEl(null);
    setSelectedUser("");
  };

  const handleCloseQrDialog = () => {
    setQrDialogOpen(false);
    setUserCredentials(null);
    setCurrentEmployee(null);
  };

  if (!groupsLoading && !isAdmin) {
    return (
      <Box>
        <Helmet>
          <title>Almond Tree Dashboard - Access Denied</title>
        </Helmet>
        <Alert severity="error" sx={{ mt: 2 }}>
          You do not have permission to view employees. Please contact an
          administrator.
        </Alert>
      </Box>
    );
  }

  if (loading) return <Typography>Loading employees...</Typography>;
  if (error) {
    return <Typography>Error loading employees: {error.message}</Typography>;
  }

  // if (loading || groupsLoading) return <Typography>Loading...</Typography>;
  // if (error) {
  //   return <Typography>Error loading employees: {error.message}</Typography>;
  // }
  return (
    <Box>
      <Helmet>
        <title>Almond Tree Dashboard - Employee List</title>
      </Helmet>

      {/* User Management Menu */}
      <Menu
        anchorEl={menuAnchorEl}
        open={Boolean(menuAnchorEl)}
        onClose={handleMenuClose}
      >
        <MenuItem
          onClick={() => handleEnableUser(selectedUser)}
          disabled={userStatusMap[selectedUser]?.userStatus == "ENABLED"}
        >
          <ListItemIcon>
            <CheckCircleIcon color="success" />
          </ListItemIcon>
          <ListItemText>Enable User</ListItemText>
        </MenuItem>
        <MenuItem
          onClick={() => handleDisableUser(selectedUser)}
          disabled={userStatusMap[selectedUser]?.userStatus == "DISABLED"}
        >
          <ListItemIcon>
            <BlockIcon color="error" />
          </ListItemIcon>
          <ListItemText>Disable User</ListItemText>
        </MenuItem>
      </Menu>

      {/* QR Code Dialog */}
      <Dialog
        open={qrDialogOpen}
        onClose={handleCloseQrDialog}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          {currentEmployee &&
          userExistsMap[
            `${currentEmployee.lastName}.${currentEmployee.firstName}`.toLowerCase()
          ]
            ? `Login Information for ${currentEmployee.firstName} ${currentEmployee.lastName}`
            : `Login Credentials for ${currentEmployee?.firstName} ${currentEmployee?.lastName}`}
        </DialogTitle>
        <DialogContent>
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              gap: 3,
            }}
          >
            {userCredentials && currentEmployee && (
              <>
                <QRCode
                  value={JSON.stringify({
                    name: `${currentEmployee.firstName} ${currentEmployee.lastName}`,
                    username: userCredentials.username,
                    loginUrl: userCredentials.loginUrl,
                    ...(userCredentials.password !==
                      "Use 'Forgot Password' if needed" && {
                      password: userCredentials.password,
                    }),
                  })}
                  size={200}
                />

                <Box sx={{ textAlign: "center" }}>
                  <Typography variant="h6" gutterBottom>
                    {userExistsMap[
                      `${currentEmployee.lastName}.${currentEmployee.firstName}`.toLowerCase()
                    ]
                      ? "Scan to login or use credentials below:"
                      : "Scan this QR code or use the credentials below:"}
                  </Typography>

                  <Typography variant="body2">
                    <strong>Username:</strong> {userCredentials.username}
                  </Typography>
                  {userCredentials.password !==
                    "Use 'Forgot Password' if needed" && (
                    <Typography variant="body2">
                      <strong>Temporary Password:</strong>{" "}
                      {userCredentials.password}
                    </Typography>
                  )}
                  <Typography variant="body2">
                    <strong>Login URL:</strong> {userCredentials.loginUrl}
                  </Typography>

                  {userCredentials.password !==
                  "Use 'Forgot Password' if needed" ? (
                    <Alert severity="warning" sx={{ mt: 2 }}>
                      User must change their password on first login!
                    </Alert>
                  ) : (
                    <Alert severity="info" sx={{ mt: 2 }}>
                      User already has an account. Use "Forgot Password" if
                      needed.
                    </Alert>
                  )}
                </Box>
              </>
            )}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseQrDialog}>Close</Button>
        </DialogActions>
      </Dialog>

      <Box sx={{ display: "flex", justifyContent: "space-between", mb: 3 }}>
        <Typography variant="h4">Employees</Typography>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={() => navigate("/dashboard/employees/new")}
          sx={{ backgroundColor: "#919749" }}
        >
          Add Employee
        </Button>
      </Box>

      {checkingUsers && (
        <Alert severity="info" sx={{ mb: 2 }}>
          Checking user accounts...
        </Alert>
      )}

      <Paper>
        {employees.map((employee: Employee) => {
          const username =
            `${employee.lastName}.${employee.firstName}`.toLowerCase();
          const userExists = userExistsMap[username] || false;
          const userStatus = userStatusMap[username];
          const isEnabled = userStatus?.userStatus == "ENABLED";

          return (
            <Box
              key={employee.id}
              sx={{
                p: 2,
                borderBottom: 1,
                borderColor: "divider",
                "&:last-child": { borderBottom: 0 },
              }}
            >
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "start",
                }}
              >
                <Box>
                  <Typography variant="h6">
                    {employee.firstName} {employee.lastName}
                    {userExists && (
                      <Chip
                        label={isEnabled ? "Active" : "Disabled"}
                        size="small"
                        color={isEnabled ? "success" : "error"}
                        sx={{ ml: 1 }}
                      />
                    )}
                  </Typography>
                  <Typography color="textSecondary">
                    {employee.email}
                  </Typography>
                  <Typography>{employee.phone}</Typography>
                  <Box sx={{ mt: 1 }}>
                    <Chip
                      label={employee.position}
                      size="small"
                      sx={{ mr: 1 }}
                    />
                    <Chip
                      label={employee.department}
                      size="small"
                      color="secondary"
                    />
                  </Box>
                </Box>
                <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                  <IconButton
                    onClick={() => navigate(`/dashboard/employees/edit/${employee.id}`)}
                    color="primary"
                    title="Edit Employee"
                  >
                    <EditIcon />
                  </IconButton>

                  <Tooltip
                    title={
                      userExists
                        ? "Show Login Information"
                        : "Create Login Account"
                    }
                  >
                    <IconButton
                      onClick={() =>
                        userExists
                          ? handleShowLogin(employee)
                          : handleCreateLogin(employee)
                      }
                      color={userExists ? "info" : "success"}
                      disabled={loadingCreate || checkingUsers}
                    >
                      {userExists ? <LoginIcon /> : <PersonAddIcon />}
                    </IconButton>
                  </Tooltip>

                  {userExists && (
                    <Tooltip title="User Management">
                      <IconButton
                        onClick={(e) => handleMenuOpen(e, username)}
                        color="default"
                      >
                        <MoreVertIcon />
                      </IconButton>
                    </Tooltip>
                  )}

                  <IconButton
                    onClick={() => handleDelete(employee.id)}
                    color="error"
                    title="Delete Employee"
                  >
                    <DeleteIcon />
                  </IconButton>
                </Box>
              </Box>
            </Box>
          );
        })}
      </Paper>
    </Box>
  );
};

export default EmployeeList;
